import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, FileText, Upload, CheckCircle, AlertTriangle,
  ArrowLeft, Download, Loader2, FileCheck, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const CSReportGenerator = () => {
  const navigate = useNavigate();
  
  // Step state: 'input' | 'processing' | 'complete' | 'error'
  const [step, setStep] = useState('input');
  
  // Company details
  const [companyName, setCompanyName] = useState('');
  const [companyType, setCompanyType] = useState('unlisted');
  const [fiscalYear, setFiscalYear] = useState('2024-25');
  
  // File upload
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Processing state
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [extractedActions, setExtractedActions] = useState([]);
  
  // Download state
  const [downloadUrls, setDownloadUrls] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const pollJobStatus = useCallback(async (jobId) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cs/job-status/${jobId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { status, progress_percent, current_step, transaction_count, case_id } = response.data;
      
      setProgress(progress_percent || 0);
      setCurrentStep(current_step || '');
      
      if (transaction_count) {
        setExtractedActions(prev => prev.length === 0 ? Array(transaction_count).fill({}) : prev);
      }
      
      if (status === 'completed' || status === 'completed_with_errors') {
        // Job complete - now generate the report
        setCurrentStep('Generating compliance opinion...');
        setProgress(95);
        
        // Generate the opinion
        const reportResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/cs/generate-opinion/${case_id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
        );
        
        if (reportResponse.data.download_url) {
          setDownloadUrls({
            docx: `${process.env.REACT_APP_BACKEND_URL}${reportResponse.data.download_url}`,
            filename: reportResponse.data.filename
          });
          setStep('complete');
          setProgress(100);
        }
      } else if (status === 'failed') {
        setStep('error');
        toast.error('Processing failed. Please try again.');
      } else {
        // Still processing - poll again
        setTimeout(() => pollJobStatus(jobId), 2000);
      }
    } catch (err) {
      console.error('Polling error:', err);
      setTimeout(() => pollJobStatus(jobId), 3000);
    }
  }, []);

  const handleGenerate = async () => {
    if (!companyName.trim()) {
      toast.error('Please enter company name');
      return;
    }
    if (files.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }

    setStep('processing');
    setUploading(true);
    setProgress(5);
    setCurrentStep('Uploading documents...');

    const token = localStorage.getItem('token');
    const formData = new FormData();
    
    formData.append('company_name', companyName);
    formData.append('company_type', companyType);
    formData.append('fiscal_year', fiscalYear);
    files.forEach(file => formData.append('files', file));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cs/upload-and-process`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000
        }
      );

      setUploading(false);
      setJobId(response.data.job_id);
      setProgress(10);
      setCurrentStep('Processing documents...');
      
      // Start polling
      pollJobStatus(response.data.job_id);
      
    } catch (err) {
      console.error('Upload error:', err);
      setStep('error');
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleDownload = async (format = 'docx') => {
    if (!downloadUrls) return;
    
    try {
      window.open(downloadUrls.docx, '_blank');
      toast.success('Download started');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const resetForm = () => {
    setStep('input');
    setFiles([]);
    setProgress(0);
    setCurrentStep('');
    setExtractedActions([]);
    setDownloadUrls(null);
    setJobId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center space-x-2">
                <FileCheck className="w-6 h-6 text-indigo-600" />
                <h1 className="text-xl font-semibold text-slate-900">
                  CS Compliance Opinion Generator
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        
        {/* Step 1: Input */}
        {step === 'input' && (
          <div className="space-y-6">
            {/* Company Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  Company Details
                </CardTitle>
                <CardDescription>
                  Enter the company information for the compliance opinion
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Enter company name"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Fiscal Year
                    </label>
                    <select
                      value={fiscalYear}
                      onChange={(e) => setFiscalYear(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      data-testid="fiscal-year-select"
                    >
                      <option value="2024-25">FY 2024-25</option>
                      <option value="2023-24">FY 2023-24</option>
                      <option value="2022-23">FY 2022-23</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company Type
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="companyType"
                        value="unlisted"
                        checked={companyType === 'unlisted'}
                        onChange={(e) => setCompanyType(e.target.value)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-slate-700">Unlisted (Companies Act Only)</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="companyType"
                        value="listed"
                        checked={companyType === 'listed'}
                        onChange={(e) => setCompanyType(e.target.value)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-slate-700">Listed (+ SEBI LODR)</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Document Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-indigo-600" />
                  Upload Documents
                </CardTitle>
                <CardDescription>
                  Upload Board Minutes, Committee Minutes, and Financial Statements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.xlsx"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                    <p className="text-slate-600 mb-2">
                      <span className="text-indigo-600 font-medium">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-sm text-slate-500">
                      PDF, DOCX, XLSX supported
                    </p>
                  </label>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">
                      {files.length} file(s) selected
                    </p>
                    {files.map((file, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700">{file.name}</span>
                          <span className="text-xs text-slate-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Document Types Help */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">Accepted Documents:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Board of Directors Minutes</li>
                    <li>• Audit Committee Minutes</li>
                    <li>• NRC / Remuneration Committee Minutes</li>
                    <li>• Finance / Borrowing Committee Minutes</li>
                    <li>• Balance Sheet with Notes to Accounts</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Generate Button */}
            <div className="flex justify-center">
              <Button
                size="lg"
                onClick={handleGenerate}
                disabled={!companyName.trim() || files.length === 0}
                className="px-8 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
                data-testid="generate-btn"
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Generate Compliance Opinion
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Processing */}
        {step === 'processing' && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                Processing Documents
              </CardTitle>
              <CardDescription>
                Please wait while we analyze your documents
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm text-slate-600 mb-2">
                  <span>{currentStep}</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-3" />
              </div>

              {/* Processing Steps */}
              <div className="space-y-3">
                {[
                  { label: 'Uploading documents', threshold: 10 },
                  { label: 'Extracting corporate actions', threshold: 40 },
                  { label: 'Applying compliance rules', threshold: 70 },
                  { label: 'Generating legal opinion', threshold: 95 }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      progress >= item.threshold 
                        ? 'bg-green-50 text-green-700' 
                        : progress >= item.threshold - 20
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'bg-slate-50 text-slate-400'
                    }`}
                  >
                    {progress >= item.threshold ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : progress >= item.threshold - 20 ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-current" />
                    )}
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* Extracted Actions Preview */}
              {extractedActions.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium text-indigo-600">{extractedActions.length}</span> corporate actions detected
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Complete */}
        {step === 'complete' && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-green-700">
                Compliance Opinion Generated Successfully
              </CardTitle>
              <CardDescription>
                Your statutory compliance report is ready for download
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Company:</span>
                    <p className="font-medium text-slate-700">{companyName}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Fiscal Year:</span>
                    <p className="font-medium text-slate-700">{fiscalYear}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Company Type:</span>
                    <p className="font-medium text-slate-700">
                      {companyType === 'listed' ? 'Listed' : 'Unlisted'}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Documents Analyzed:</span>
                    <p className="font-medium text-slate-700">{files.length}</p>
                  </div>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  onClick={() => handleDownload('docx')}
                  className="px-8 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="download-docx-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Word Report
                </Button>
              </div>

              {/* New Report Button */}
              <div className="text-center pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={resetForm}
                  data-testid="new-report-btn"
                >
                  <ChevronRight className="w-4 h-4 mr-2" />
                  Generate Another Report
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {step === 'error' && (
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <CardTitle className="text-red-700">
                Processing Failed
              </CardTitle>
              <CardDescription>
                There was an error generating your compliance opinion
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={resetForm} variant="outline">
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CSReportGenerator;
